import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { verificationAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const DOC_STATUS = {
  approved: { color: '#059669', bg: 'rgba(16,185,129,0.08)', label: 'Approved' },
  rejected: { color: '#DC2626', bg: 'rgba(239,68,68,0.08)', label: 'Rejected' },
  needs_review: { color: '#D97706', bg: 'rgba(245,158,11,0.08)', label: 'Needs Review' },
  pending: { color: '#0076A3', bg: 'rgba(30,104,255,0.08)', label: 'Pending' },
};

export default function VerificationDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const loadData = useCallback(async () => {
    try {
      // We need to get the request and its documents
      // Use the dashboard endpoint and find the request
      const dashRes = await verificationAPI.getDashboard();
      const req = dashRes.data.requests.find(r => r._id === requestId);
      if (req) {
        setRequest(req);
        // Full documents via the recruiter-only, ownership-scoped endpoint.
        const docsRes = await verificationAPI.listRequestDocuments(requestId);
        setDocuments(docsRes.data.documents || []);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally { setLoading(false); }
  }, [requestId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh while documents are pending
  useEffect(() => {
    const hasPending = documents.some(d => d.status === 'pending');
    if (!hasPending) return;
    const iv = setInterval(loadData, 3000);
    return () => clearInterval(iv);
  }, [documents, loadData]);

  const handleReview = async (action) => {
    if (!reviewDoc) return;
    try {
      await verificationAPI.reviewDocument(reviewDoc._id, { action, reason: rejectReason });
      toast.success(`Document ${action}d`);
      setReviewDoc(null);
      setRejectReason('');
      loadData();
    } catch (err) { toast.error('Review failed'); }
  };

  const copyLink = () => {
    if (request?.access_code) {
      navigator.clipboard.writeText(`${window.location.origin}/verify/${request.access_code}`);
      toast.success('Verification link copied!');
    }
  };

  if (loading) return <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'60vh',color:'#64748B' }}>Loading...</div>;
  if (!request) return <div style={{ textAlign:'center',padding:'4rem',color:'#64748B' }}>Request not found</div>;

  const clearanceCfg = {
    cleared: { color: '#059669', bg: 'rgba(16,185,129,0.08)' },
    pending: { color: '#0076A3', bg: 'rgba(30,104,255,0.08)' },
    action_required: { color: '#D97706', bg: 'rgba(245,158,11,0.08)' },
    blocked: { color: '#DC2626', bg: 'rgba(239,68,68,0.08)' },
  }[request.clearance_status] || { color: '#64748B', bg: '#F1EAE1' };

  return (
    <div data-testid="verification-detail-page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate('/verification')} style={{ background:'none',border:'none',color:'#64748B',cursor:'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div>
            <h1 className="page-title">{request.candidate_name}</h1>
            <p className="page-subtitle">{request.candidate_email} &middot; {request.job_title}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button variant="outline" onClick={copyLink} style={{ borderRadius: 10, fontSize: '0.8125rem' }}>Copy Candidate Link</Button>
        </div>
      </div>

      {/* Clearance Status Banner */}
      <Card style={{ background: clearanceCfg.bg, border: `1px solid ${clearanceCfg.color}20`, borderRadius: 14, padding: '1.25rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: clearanceCfg.color, textTransform: 'capitalize', marginBottom: '0.25rem' }}>
              {request.clearance_status?.replace('_', ' ')}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#64748B' }}>{request.clearance_message}</div>
          </div>
          <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: clearanceCfg.color }}>
            {documents.filter(d => d.status === 'approved').length}/{(request.requirements || []).filter(r => r.is_mandatory).length}
          </div>
        </div>
        {(request.clearance_reasons || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginTop: '0.75rem' }}>
            {request.clearance_reasons.map((r, i) => (
              <span key={`cr-${i}`} style={{ padding: '0.1875rem 0.5rem', borderRadius: 999, background: '#FEF3C7', border: '1px solid #FDE68A', fontSize: '0.6875rem', color: '#92400E' }}>{r}</span>
            ))}
          </div>
        )}
      </Card>

      {/* Requirements + Documents */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Required Documents */}
        <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#002A38' }}>Required Documents</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {(request.requirements || []).map((req_item, i) => {
              const uploaded = documents.find(d => d.document_type === req_item.document_type);
              const statusCfg = uploaded ? (DOC_STATUS[uploaded.status] || DOC_STATUS.pending) : null;
              return (
                <div key={`req-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', borderRadius: 10, border: '1px solid #ECE7DE', background: '#FAFBFC' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#002A38' }}>
                      {req_item.document_type}
                      {req_item.is_mandatory && <span style={{ color: '#DC2626', marginLeft: 4 }}>*</span>}
                    </div>
                    <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>{req_item.description}</div>
                  </div>
                  {uploaded ? (
                    <Badge style={{ background: statusCfg.bg, color: statusCfg.color, borderRadius: 999, fontSize: '0.625rem' }}>{statusCfg.label}</Badge>
                  ) : (
                    <Badge style={{ background: '#F1EAE1', color: '#94A3B8', borderRadius: 999, fontSize: '0.625rem' }}>Not Submitted</Badge>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Submitted Documents */}
        <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#002A38' }}>Submitted Documents ({documents.length})</h3>
          {documents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#94A3B8', fontSize: '0.875rem' }}>No documents uploaded yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {documents.map(doc => {
                const cfg = DOC_STATUS[doc.status] || DOC_STATUS.pending;
                return (
                  <div key={doc._id} style={{ padding: '0.75rem', borderRadius: 10, border: '1px solid #ECE7DE', background: '#FAFBFC' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#002A38' }}>{doc.document_type}</div>
                      <Badge style={{ background: cfg.bg, color: cfg.color, borderRadius: 999, fontSize: '0.625rem' }}>{cfg.label}</Badge>
                    </div>
                    <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginBottom: '0.5rem' }}>
                      {doc.filename} &middot; {doc.expiry_date ? `Expires: ${doc.expiry_date}` : 'No expiry'}
                    </div>
                    {doc.validation_result?.summary && (
                      <div style={{ fontSize: '0.75rem', color: '#64748B', fontStyle: 'italic', marginBottom: '0.5rem' }}>{doc.validation_result.summary}</div>
                    )}
                    {doc.status !== 'approved' && (
                      <div style={{ display: 'flex', gap: '0.375rem' }}>
                        <Button onClick={() => { setReviewDoc(doc); handleReview('approve'); }} style={{ background: '#059669', color: '#fff', borderRadius: 8, fontSize: '0.6875rem', padding: '0.25rem 0.5rem' }}>Approve</Button>
                        <Button variant="outline" onClick={() => setReviewDoc(doc)} style={{ borderRadius: 8, fontSize: '0.6875rem', padding: '0.25rem 0.5rem', borderColor: '#FCA5A5', color: '#DC2626' }}>Reject</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Reject Dialog */}
      <Dialog open={!!reviewDoc && reviewDoc.status !== 'approved'} onOpenChange={() => setReviewDoc(null)}>
        <DialogContent style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, maxWidth: 400 }}>
          <DialogHeader><DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Reject Document</DialogTitle></DialogHeader>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: '0.375rem' }}>Reason for rejection</label>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} placeholder="Explain why this document is being rejected..."
              style={{ width: '100%', padding: '0.625rem', borderRadius: 10, border: '1px solid #CBD5E1', background: '#FAF7F2', color: '#002A38', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDoc(null)} style={{ borderRadius: 10 }}>Cancel</Button>
            <Button onClick={() => handleReview('reject')} style={{ background: '#DC2626', color: '#fff', borderRadius: 10, fontWeight: 600 }}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
